import React from 'react';

import { theme } from '~/theme';

import InfoIcon from '@mui/icons-material/Info';
import { Grid, IconButton, Stack, Typography } from '@mui/material';

import useToggleState from '~/hooks/useToggleState';

import { AvailabilityDebugResponse } from '~/services/AccommodationService';

import MissingRatesModal from './MissingRatesModal';
import PricingIssuesModal from './PricingIssuesModal';

interface DebugInformationProps {
  availabilityDebugResponse: AvailabilityDebugResponse;
}
export default function DebugInformation({ availabilityDebugResponse }: DebugInformationProps) {
  const { debug } = availabilityDebugResponse;
  const {
    isToggled: missingRatesModalOpen,
    toggleOn: openMissingRatesModal,
    toggleOff: closeMissingRatesModal,
  } = useToggleState(false);

  const {
    isToggled: pricingIssuesModalOpen,
    toggleOn: openPricingIssuesModal,
    toggleOff: closePricingIssuesModal,
  } = useToggleState(false);

  if (!debug) {
    // No debug information available
    return null;
  }

  if (debug.missingRates.length === 0 && debug.pricingIssues.length === 0) {
    // No missing rates or pricing issues, no need to show full debug information
    return (
      <Stack
        direction="column"
        spacing={2}
        sx={{
          backgroundColor: theme.palette.warning.light,
          padding: 2,
          borderRadius: 1,
        }}
      >
        <Typography variant="h6" gutterBottom fontWeight="bold" sx={{ color: theme.palette.primary.contrastText }}>
          Debug Information
        </Typography>
        <Typography variant="subtitle2" sx={{ color: theme.palette.primary.contrastText, opacity: 0.9 }}>
          No missing rates or pricing issues detected.
        </Typography>
      </Stack>
    );
  }
  return (
    <>
      <Stack
        direction="column"
        spacing={2}
        sx={{
          backgroundColor: theme.palette.warning.light,
          padding: 2,
          borderRadius: 1,
        }}
      >
        <Typography variant="h6" gutterBottom fontWeight="bold" sx={{ color: theme.palette.primary.contrastText }}>
          Debug Information
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={3}>
            <Typography variant="subtitle2" sx={{ color: theme.palette.primary.contrastText, opacity: 0.9 }}>
              Pricing Issues
            </Typography>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography sx={{ color: theme.palette.primary.contrastText }}>{debug.pricingIssues.length}</Typography>
              <IconButton size="small" onClick={openPricingIssuesModal}>
                <InfoIcon style={{ color: theme.palette.primary.contrastText }} />
              </IconButton>
            </Stack>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Typography variant="subtitle2" sx={{ color: theme.palette.primary.contrastText, opacity: 0.9 }}>
              Missing Rates
            </Typography>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography sx={{ color: theme.palette.primary.contrastText }}>{debug.missingRates.length}</Typography>
              <IconButton size="small" onClick={openMissingRatesModal}>
                <InfoIcon style={{ color: theme.palette.primary.contrastText }} />
              </IconButton>
            </Stack>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Typography variant="subtitle2" sx={{ color: theme.palette.primary.contrastText, opacity: 0.9 }}>
              Accomm Rates
            </Typography>
            <Typography sx={{ color: theme.palette.primary.contrastText }}>{debug.numberOfAccommRates}</Typography>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Typography variant="subtitle2" sx={{ color: theme.palette.primary.contrastText, opacity: 0.9 }}>
              Calendar Rates
            </Typography>
            <Typography sx={{ color: theme.palette.primary.contrastText }}>{debug.numberOfCalendarRates}</Typography>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Typography variant="subtitle2" sx={{ color: theme.palette.primary.contrastText, opacity: 0.9 }}>
              Reservation Rates Processed
            </Typography>
            <Typography sx={{ color: theme.palette.primary.contrastText }}>
              {debug.rawRatesReceivedFromService.reservation}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Typography variant="subtitle2" sx={{ color: theme.palette.primary.contrastText, opacity: 0.9 }}>
              Bedbank Rates Processed
            </Typography>
            <Typography sx={{ color: theme.palette.primary.contrastText }}>
              {debug.rawRatesReceivedFromService.bedbank}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Typography variant="subtitle2" sx={{ color: theme.palette.primary.contrastText, opacity: 0.9 }}>
              Winning Reservation Rates
            </Typography>
            <Typography sx={{ color: theme.palette.primary.contrastText }}>
              {debug.numberOfWinningReservationRates}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Typography variant="subtitle2" sx={{ color: theme.palette.primary.contrastText, opacity: 0.9 }}>
              Winning Bedbank Rates
            </Typography>
            <Typography sx={{ color: theme.palette.primary.contrastText }}>
              {debug.numberOfWinningBedbankRates}
            </Typography>
          </Grid>
        </Grid>
      </Stack>
      {missingRatesModalOpen && (
        <MissingRatesModal availabilityDebugResponse={availabilityDebugResponse} closeModal={closeMissingRatesModal} />
      )}
      {pricingIssuesModalOpen && (
        <PricingIssuesModal
          availabilityDebugResponse={availabilityDebugResponse}
          closeModal={closePricingIssuesModal}
        />
      )}
    </>
  );
}
